import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import DisallowedTransactionsFrame from "./DisallowedTransactionsFrame";
import CapitalGainTransactionsFrame from "./CapitalGainTransactionsFrame";
import CapitalLossTransactionsFrame from "./CapitalLossTransactionsFrame";
import TaxLossOpportunitiesFrame from "./TaxLossOpportunitiesFrame";
import UnrealizedCapitalGainsFrame from "./UnrealizedCapitalGainsFrame";
import WashSaleAvoidance from "./WashSaleAvoidance";
import "./Dashboard.css";
import { PrimeReactProvider } from "primereact/api";
import { useNavigate, useLocation } from "react-router-dom";
import "primereact/resources/themes/saga-green/theme.css";
import OnboardingProgress from './OnboardingProgress';
import { useTransactions, invalidateAllTransactionCaches, hasCachedData, getCachedDataForEndpoint } from './useTransactions';
import { useSettledTransactions } from './useSettledTransactions';
import TransactionProgressIndicator from './TransactionProgressIndicator';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import unlockService from '../services/unlockService';
import { handleUnauthorized } from '../utils/api';
import { getPortfolioValue } from '../api/portfolio';
import { getAccounts, getSyncStatus } from '../api/accounts';
import { createLinkToken } from '../api/plaid';
import { logout as apiLogout } from '../api/auth';
import apiClient from '../api/client';
import AccountConnectionModal from './AccountConnectionModal';
import PlaidLinkComponent from './PlaidLinkComponent';
import { Tooltip } from 'primereact/tooltip';
import '../css/FrameStyles.css';

function Dashboard({ username, setModalOpen, setIsOnboardingProcessing, isOnboardingProcessing }) {
  const [hasAccounts, setHasAccounts] = useState(false);
  const [accountsCheckComplete, setAccountsCheckComplete] = useState(false); // Track if initial accounts check is done
  const [activeMainTab, setActiveMainTab] = useState("unrealized");
  const [activeSubTab, setActiveSubTab] = useState("unrealizedLosses");
  const navigate = useNavigate();
  const location = useLocation();
  const hasNotifiedRef = useRef(false);
  
  // Account connection modal states
  const [showAccountConnectionModal, setShowAccountConnectionModal] = useState(false);
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  
  // File upload states (multiple files; CSV and PDF only)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [brokerageName, setBrokerageName] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showUploadOption, setShowUploadOption] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);

  // Sequential API polling states
  const [accountSyncStatus, setAccountSyncStatus] = useState(false);
  const [potentialLossOpportunities, setPotentialLossOpportunities] = useState([]);
  const [currentPollingPhase, setCurrentPollingPhase] = useState('accountSync'); // 'accountSync', 'settledTransactions', 'lossOpportunities'
  
  // Dashboard unlock states
  const [isDashboardUnlocked, setIsDashboardUnlocked] = useState(true);
  const [hasFreeAccess, setHasFreeAccess] = useState(true);
  const [userTier, setUserTier] = useState('STANDARD');
  const [unlockStatusLoading, setUnlockStatusLoading] = useState(false);

  // Portfolio value (displayed in header when hasAccounts)
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(null);
  const [portfolioValueLoading, setPortfolioValueLoading] = useState(false);
  
  // Polling intervals using useRef to avoid dependency issues
  const accountSyncIntervalRef = useRef(null);
  const cacheInitializedRef = useRef(false);
  const fileInputRef = useRef(null);

  
  // Use new async settled transactions hook
  const shouldFetchSettledTransactions = hasAccounts && currentPollingPhase === 'settledTransactions';
  const { 
    transactions: settledTransactions, 
    loading: settledTransactionsLoading, 
    error: settledTransactionsError,
    progress: settledTransactionsProgress 
  } = useSettledTransactions(username, shouldFetchSettledTransactions);

  // Function to check and show connection status toast
  const checkAndShowConnectionToast = useCallback((statusData = null) => {
    if (location.pathname === '/tax-insights') {
      let status = statusData;
      
      // If no status provided, check localStorage
      if (!status) {
        const connectionStatus = localStorage.getItem('snaptrade_connection_status');
        console.log('Checking for connection status:', connectionStatus);
        if (connectionStatus) {
          try {
            status = JSON.parse(connectionStatus);
          } catch (err) {
            console.error('Error parsing connection status:', err);
            localStorage.removeItem('snaptrade_connection_status');
            return;
          }
        } else {
          return;
        }
      }
      
      if (status) {
        console.log('Connection status found:', status);
        if (status.type === 'success') {
          toast.success(status.message);
        } else if (status.type === 'warning') {
          toast.warning(status.message);
        } else if (status.type === 'error') {
          toast.error(status.message);
        }
        // Clear the status after showing
        localStorage.removeItem('snaptrade_connection_status');
      }
    }
  }, [location.pathname]);

  // Check for SnapTrade connection status on mount and when route changes
  useEffect(() => {
    // Small delay to ensure ToastContainer is mounted
    const timer = setTimeout(() => {
      checkAndShowConnectionToast();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Listen for SnapTrade exit to reopen AccountConnectionModal
  useEffect(() => {
    const handleSnapTradeExit = (e) => {
      if (e.detail?.shouldReopenConnectionModal) {
        setShowAccountConnectionModal(true);
      }
    };

    window.addEventListener('snaptradeExited', handleSnapTradeExit);
    return () => {
      window.removeEventListener('snaptradeExited', handleSnapTradeExit);
    };
  }, []);

  // Listen for custom event and window events (when SnapTrade window closes)
  useEffect(() => {
    // Listen for custom event dispatched when connection completes
    const handleConnectionComplete = (e) => {
      console.log('SnapTrade connection complete event received:', e.detail);
      if (e.detail) {
        checkAndShowConnectionToast(e.detail);
      }
    };

    const handleWindowFocus = () => {
      // Small delay to allow localStorage to be updated
      setTimeout(() => {
        checkAndShowConnectionToast();
      }, 500);
    };

    const handleVisibilityChange = () => {
      // Check when page becomes visible again (when popup closes)
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          checkAndShowConnectionToast();
        }, 500);
      }
    };

    // Listen for storage events (localStorage changes from other windows)
    const handleStorageChange = (e) => {
      if (e.key === 'snaptrade_connection_status') {
        setTimeout(() => {
          checkAndShowConnectionToast();
        }, 100);
      }
    };

    window.addEventListener('snaptradeConnectionComplete', handleConnectionComplete);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('snaptradeConnectionComplete', handleConnectionComplete);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAndShowConnectionToast]);

  // Fetch current portfolio value when user has accounts
  useEffect(() => {
    if (!hasAccounts) return;
    let cancelled = false;
    const fetchPortfolioValue = async () => {
      try {
        setPortfolioValueLoading(true);
        const data = await getPortfolioValue();
        if (!cancelled && data != null && typeof data.totalValue === 'number') {
          setTotalPortfolioValue(data.totalValue);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching current portfolio value:", err);
          setTotalPortfolioValue(null);
        }
      } finally {
        if (!cancelled) setPortfolioValueLoading(false);
      }
    };
    fetchPortfolioValue();
    return () => { cancelled = true; };
  }, [hasAccounts]);


  // Polling effect for onboarding progress - updated to use new sequential approach
  // Check for cached data first - if cached data exists, don't show onboarding progress
  useEffect(() => {
    if (!hasAccounts) {
      return; // No accounts yet, don't change onboarding state
    }

    // Check if we have cached data for key endpoints
    const hasCachedSettledTransactions = hasCachedData('transactions/getSettledTransactions');
    const hasCachedLossOpportunities = hasCachedData('transactions/getPotentialLossOpportunities');
    
    // If we have cached data, we're not processing - show dashboard immediately
    if (hasCachedSettledTransactions || hasCachedLossOpportunities) {
      setIsOnboardingProcessing(false);
      return;
    }

    // If we have actual data loaded (not just cached), we're done
    if (currentPollingPhase === 'lossOpportunities' && potentialLossOpportunities.length > 0) {
      setIsOnboardingProcessing(false);
    } else if (settledTransactions && settledTransactions.length > 0) {
      // We have settled transactions, but might still be processing loss opportunities
      // Only show onboarding if we're actively loading and don't have loss opportunities yet
      if (settledTransactionsLoading || (currentPollingPhase === 'lossOpportunities' && potentialLossOpportunities.length === 0)) {
        // Still processing - keep showing onboarding only if actively loading
        setIsOnboardingProcessing(settledTransactionsLoading);
      } else {
        setIsOnboardingProcessing(false);
      }
    } else if (hasAccounts) {
      // We have accounts but no data yet - check if we're actively loading
      // Only show onboarding if we're actually loading, not if we're just waiting
      setIsOnboardingProcessing(settledTransactionsLoading || currentPollingPhase !== 'accountSync');
    }
  }, [hasAccounts, currentPollingPhase, potentialLossOpportunities.length, settledTransactions, settledTransactionsLoading]);

  // Poll for linked accounts if none are found (only after initial check is complete)
  useEffect(() => {
    if (hasAccounts || !accountsCheckComplete) return; // Don't poll until initial check is done
    let interval = setInterval(async () => {
      try {
        const data = await getAccounts();
        const hasEnabledAccount = Array.isArray(data) && data.some((acc) => acc.enabled === true);
        if (hasEnabledAccount) {
          console.log('Polling: Setting hasAccounts to true, found at least one enabled account');
          setHasAccounts(true);
        }
      } catch (err) {
        console.error('Error polling for accounts:', err);
        // Optionally handle error
      }
    }, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [hasAccounts, accountsCheckComplete]);

  useEffect(() => {
    // Don't fetch accounts if username is not available yet
    if (!username) {
      return;
    }

    let isCompleted = false;
    
    // Add timeout to prevent infinite loading (safety net)
    const timeoutId = setTimeout(() => {
      if (!isCompleted) {
        isCompleted = true;
        setAccountsCheckComplete(true);
        setHasAccounts(false);
      }
    }, 5000); // 5 second timeout - prevent infinite loading
    
    async function getAccountsList(retryCount = 0) {
      try {
        // Small delay on first attempt to ensure session is ready
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        const data = await getAccounts();
        const hasEnabledAccount = Array.isArray(data) && data.some((acc) => acc.enabled === true);
        if (Array.isArray(data) && hasEnabledAccount) {
          if (!isCompleted) {
            isCompleted = true;
            clearTimeout(timeoutId);
            setHasAccounts(true);
            setAccountsCheckComplete(true);
          }
        } else {
          if (!isCompleted) {
            isCompleted = true;
            clearTimeout(timeoutId);
            // No accounts found - mark check as complete
            setHasAccounts(false);
            setAccountsCheckComplete(true);
          }
        }
      } catch (err) {
        // Retry if it's a 401 or network error (session might not be ready yet)
        if ((err.response?.status === 401 || !err.response) && retryCount < 2 && !isCompleted) {
          await new Promise(resolve => setTimeout(resolve, 800));
          return getAccountsList(retryCount + 1);
        }
        
        // On error after retries, mark check as complete and assume no accounts (polling will handle retries)
        if (!isCompleted) {
          isCompleted = true;
          clearTimeout(timeoutId);
          setAccountsCheckComplete(true);
          setHasAccounts(false);
        }
      }
    }
    
    getAccountsList();
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [username]); // Depend on username so it waits for it to be available

  // Reset notification flag when a new account is linked and invalidate cache
  const prevHasAccounts = useRef(hasAccounts);
  useEffect(() => {
    if (!prevHasAccounts.current && hasAccounts) {
      hasNotifiedRef.current = false;
      // Invalidate cache when a new account is linked
      console.log('New account linked - invalidating transaction caches');
      invalidateAllTransactionCaches();
    }
    prevHasAccounts.current = hasAccounts;
  }, [hasAccounts]);

  // Notify backend when dashboard is ready after new account
  useEffect(() => {
    const dashboardReady =
      !accountSyncStatus && // Check if account sync is not yet complete
      (
        (settledTransactions && Array.isArray(settledTransactions) && settledTransactions.length > 0) ||
        (potentialLossOpportunities && Array.isArray(potentialLossOpportunities) && potentialLossOpportunities.length > 0)
      );
    if (
      dashboardReady &&
      !hasNotifiedRef.current
    ) {
      hasNotifiedRef.current = true;
      // notify-dashboard-ready removed in v1 API
    }
  }, [accountSyncStatus, settledTransactions, potentialLossOpportunities]);

  // Check account sync status every 15 seconds
  useEffect(() => {
    if (!hasAccounts) return;

    const checkAccountSync = async () => {
      try {
        const response = { data: await getSyncStatus() };
        
        if (response.data === true) {
          // Invalidate cache when account sync completes
          const prevSyncStatus = accountSyncStatus;
          setAccountSyncStatus(true);
          
          // Only invalidate if this is a new sync completion (transition from false to true)
          if (!prevSyncStatus) {
            console.log('Account sync completed - invalidating transaction caches');
            invalidateAllTransactionCaches();
          }
          
          setCurrentPollingPhase('settledTransactions');
          // Clear account sync interval and start settled transactions polling
          if (accountSyncIntervalRef.current) {
            clearInterval(accountSyncIntervalRef.current);
            accountSyncIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error("Error checking account sync status:", err);
      }
    };

    // Initial check
    checkAccountSync();
    
    // Set up polling every 15 seconds
    const interval = setInterval(checkAccountSync, 15000);
    accountSyncIntervalRef.current = interval;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hasAccounts, accountSyncStatus]);

  // Monitor settled transactions and move to next phase when ready
  useEffect(() => {
    if (currentPollingPhase === 'settledTransactions' && settledTransactions && settledTransactions.length > 0) {
      setCurrentPollingPhase('lossOpportunities');
    }
  }, [currentPollingPhase, settledTransactions]);

  // Initialize data from cache on mount if available - this ensures instant display when navigating back
  useEffect(() => {
    if (cacheInitializedRef.current) return; // Already initialized
    cacheInitializedRef.current = true;
    
    // Check for cached loss opportunities first (most important for showing dashboard)
    const cachedLossOpportunities = getCachedDataForEndpoint('transactions/getPotentialLossOpportunities');
    if (cachedLossOpportunities && Array.isArray(cachedLossOpportunities) && cachedLossOpportunities.length > 0) {
      setPotentialLossOpportunities(cachedLossOpportunities);
      setCurrentPollingPhase('lossOpportunities');
      setIsOnboardingProcessing(false);
      return; // We have the key data, dashboard can show immediately
    }
    
    // If no loss opportunities in cache, check for settled transactions
    // This helps set the right polling phase
    const cachedSettledTransactions = getCachedDataForEndpoint('transactions/getSettledTransactions');
    if (cachedSettledTransactions && Array.isArray(cachedSettledTransactions) && cachedSettledTransactions.length > 0) {
      // Note: settledTransactions state is managed by useSettledTransactions hook
      // But we can set the polling phase to indicate we're ready for loss opportunities
      setCurrentPollingPhase('lossOpportunities');
    }
  }, []); // Only run on mount

  // Get potential loss opportunities once settled transactions are available
  useEffect(() => {
    if (currentPollingPhase !== 'lossOpportunities' || !settledTransactions || settledTransactions.length === 0) return;
    
    // Check cache first - if we have cached data, use it instead of fetching
    const cachedLossOpportunities = getCachedDataForEndpoint('transactions/getPotentialLossOpportunities');
    if (cachedLossOpportunities && Array.isArray(cachedLossOpportunities) && cachedLossOpportunities.length > 0) {
      setPotentialLossOpportunities(cachedLossOpportunities);
      setIsOnboardingProcessing(false);
      return;
    }

    const getLossOpportunities = async () => {
      try {
        const data = await apiClient.get('/api/v1/tax/harvest/opportunities', { params: { type: 'loss' } });
        setPotentialLossOpportunities(data.data || []);
        setIsOnboardingProcessing(false);
      } catch (err) {
        console.error("Error getting potential loss opportunities:", err);
      }
    };

    getLossOpportunities();
  }, [currentPollingPhase, settledTransactions.length, setIsOnboardingProcessing]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (accountSyncIntervalRef.current) clearInterval(accountSyncIntervalRef.current);
    };
  }, []);

  // Handler to continue to dashboard even without transactions
  const handleContinueToDashboard = () => {
    setIsOnboardingProcessing(false);
    // Navigate to tax-insights to show the dashboard
    navigate('/tax-insights', { replace: true });
  };

  // Handler to open account connection modal
  const handleConnectAccountsFromOnboarding = () => {
    setIsOnboardingProcessing(false);
    setShowAccountConnectionModal(true);
  };

  // Only show onboarding progress if there is at least one account
  // But also check if accounts exist in the response even if hasAccounts state hasn't updated yet
  const shouldShowOnboarding = isOnboardingProcessing && hasAccounts;
  if (shouldShowOnboarding) {
    return <OnboardingProgress 
      checkStatus={async () => {
        return (
          (settledTransactions && settledTransactions.length > 0) ||
          (potentialLossOpportunities && potentialLossOpportunities.length > 0)
        );
      }}
      username={username}
      hasAccounts={hasAccounts}
      onContinueToDashboard={handleContinueToDashboard}
      onConnectAccounts={handleConnectAccountsFromOnboarding}
    />;
  }

  const tabConfig = {
    realized: {
      label: "Realized",
      subTabs: {
        realizedGains: "Realized Gains",
        realizedLosses: "Realized Losses",
      },
    },
    unrealized: {
      label: "Unrealized",
      subTabs: {
        unrealizedGains: "Unrealized Gains",
        unrealizedLosses: "Unrealized Losses",
      },
    },
    washSales: {
      label: "Wash Sales",
      subTabs: {
        washSaleAvoidance: "Wash Sale Avoidance",
        disallowedTransactions: "Past Wash Sales",
      },
    },
  };

  const handleMainTabChange = (mainTab) => {
    setActiveMainTab(mainTab);
    setActiveSubTab(Object.keys(tabConfig[mainTab].subTabs)[0]);
  };

  const handleAddFirstAccount = () => {
    console.log("handleAddFirstAccount method is being called");
    setShowAccountConnectionModal(true);
  };

  const handleManualUploadSelect = () => {
    setShowAccountConnectionModal(false);
    setShowUploadOption(true);
  };

  const handleSnapTradeSelect = () => {
    setModalOpen({
      open: true,
      type: "account",
    });
  };

  const handlePlaidSelect = async () => {
    // Pre-fetch Plaid link token before opening modal to skip loading screen
    try {
      const data = await createLinkToken();
      if (data && data.linkToken) {
        setPlaidLinkToken(data.linkToken);
        setShowPlaidModal(true);
      } else {
        toast.error('Failed to initialize Plaid. Please try again.');
      }
    } catch (error) {
      console.error('Error getting Plaid link token:', error);
      toast.error('Failed to initialize Plaid. Please try again.');
    }
  };

  const handlePlaidSuccess = (result) => {
    console.log('Plaid account connected:', result);
    setShowPlaidModal(false);
    toast.success('Account connected successfully!');
    // Refresh accounts
    window.location.reload();
  };

  const handlePlaidClose = () => {
    setShowPlaidModal(false);
    setPlaidLinkToken(null); // Reset token when closing
    // Reopen AccountConnectionModal when Plaid is closed
    setShowAccountConnectionModal(true);
  };

  const handleExitDemo = async () => {
    try {
      // Call backend logout endpoint to clear session
      await apiLogout();
    } catch (error) {
      console.error("Logout request failed:", error);
      // Continue with logout even if backend request fails
    }
    
    // Use centralized handler to clear all session data (localStorage, sessionStorage, cookies)
    handleUnauthorized({ suppressRedirect: true });
    
    // Navigate to landing page
    navigate("/");
    
    // Reload to ensure all state is cleared
    window.location.reload();
  };

  const handleUnlockSuccess = async (paymentIntent, unlockFee, savings) => {
    console.log('Dashboard unlocked successfully:', { paymentIntent, unlockFee, savings });
    
    // Wait a moment for webhook to process, then check backend status
    setTimeout(async () => {
      try {
        console.log('Checking backend unlock status...');
        const status = await unlockService.checkUnlockStatus();
        console.log('Backend unlock status:', status);
        
        setIsDashboardUnlocked(status.unlocked);
        setHasFreeAccess(status.hasFreeAccess);
        
        if (status.unlocked) {
          toast.success(`Dashboard unlocked! You can now see detailed transaction insights.`);
        } else {
          toast.warning(`Payment processed, but unlock status is still being updated. Please refresh the page in a moment.`);
        }
      } catch (error) {
        console.error('Error checking unlock status:', error);
        toast.error('Payment succeeded but unable to verify unlock status. Please refresh the page.');
      }
    }, 2000); // Wait 2 seconds for webhook to process
  };

  // Allowed statement file types: CSV and PDF only
  const ALLOWED_STATEMENT_EXTENSIONS = ['.csv', '.pdf'];
  const ALLOWED_STATEMENT_TYPES = ['text/csv', 'application/pdf', 'application/vnd.ms-excel'];

  const isAllowedStatementFile = (file) => {
    const name = (file.name || '').toLowerCase();
    const ext = ALLOWED_STATEMENT_EXTENSIONS.some((e) => name.endsWith(e));
    const type = ALLOWED_STATEMENT_TYPES.includes(file.type);
    return ext || type;
  };

  const handleFileUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setUploadError("Please select at least one file to upload");
      toast.error("Please select at least one file to upload");
      return;
    }

    const invalid = selectedFiles.filter((f) => !isAllowedStatementFile(f));
    if (invalid.length > 0) {
      const names = invalid.map((f) => f.name).join(", ");
      setUploadError(`Invalid file type. Only CSV and PDF are allowed. Please remove: ${names}`);
      toast.error("Only CSV and PDF files are allowed.");
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadResult(null);

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    if (brokerageName && brokerageName.trim() !== "") {
      formData.append("broker", brokerageName.trim());
    }

    // chatbot upload moved to /api/v2/chat — update when v2 chat is integrated
    const uploadUrl = '/api/v2/chat/upload-transactions';
    try {
      const response = await apiClient.post(uploadUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Upload response:", response.data);

      const payload = response?.data || {};

      if (payload?.error) {
        setUploadError(payload.error);
        toast.error(payload.error);
        return;
      }

      setUploadResult(payload);
      setShowUploadResultModal(true);
    } catch (err) {
      console.error("Error uploading file:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error data:", err.response?.data);
      
      if (err.response?.status === 404) {
        const errorMsg = "Upload endpoint not found. Please contact support or try linking your account instead.";
        setUploadError(errorMsg);
        toast.error(errorMsg);
      } else if (err.response?.status === 500) {
        const errorMsg = "Server error. Please try again or contact support.";
        setUploadError(errorMsg);
        toast.error(errorMsg);
      } else {
        const errorMsg = err.response?.data?.error || "Error uploading file. Please try again.";
        setUploadError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const proceedFromUploadResult = () => {
    setShowUploadResultModal(false);

    // Proceed to main dashboard view
    setHasAccounts(true);
    setIsOnboardingProcessing(false);
    setShowUploadOption(false);
    setSelectedFiles([]);
    setBrokerageName("");

    console.log('File uploaded - invalidating transaction caches');
    invalidateAllTransactionCaches();

    toast.success("Upload received! Processing your transactions...");
  };

  const cancelUploadResult = () => {
    setShowUploadResultModal(false);
  };

  // Example polling function (replace with your real API call)
  const checkStatus = async () => {
    // Replace with your backend endpoint
    const res = await fetch('/api/onboarding/status', { credentials: 'include' });
    const data = await res.json();
    return data.ready;
  };

  return (
    <>
      {hasAccounts ? (
        <PrimeReactProvider>
          <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="dashboard-container">
        <div className="content-wrapper">
          <div className="dashboard-header">
            <div className="dashboard-header-right">
              <div
                className="dashboard-header-portfolio-value portfolio-value-tooltip-trigger"
                data-pr-tooltip="This might be delayed by 24-48 hours depending on data aggregator data."
                data-pr-position="top"
                data-pr-at="center bottom+10"
                data-pr-my="center top"
              >
                {portfolioValueLoading ? (
                  <span className="portfolio-value-loading">Loading...</span>
                ) : totalPortfolioValue != null ? (
                  <>
                    <span className="portfolio-value-label">Total portfolio value: </span>
                    <span className="portfolio-value-amount">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalPortfolioValue)}
                    </span>
                  </>
                ) : null}
              </div>
              <Tooltip
                target=".portfolio-value-tooltip-trigger"
                position="top"
                mouseTrack={false}
                showDelay={50}
                hideDelay={100}
              />
              <div className="header-buttons">
                {username !== "demo" && (
                  <button
                    className="btn-punchy btn-punchy-secondary"
                    onClick={handleAddFirstAccount}
                  >
                    Connect Accounts
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="tabs-wrapper">
            <div className="main-tabs">
              {Object.keys(tabConfig).map((mainTab) => (
                <button
                  key={mainTab}
                  onClick={() => handleMainTabChange(mainTab)}
                  className={activeMainTab === mainTab ? "active" : ""}
                >
                  {tabConfig[mainTab].label}
                </button>
              ))}
            </div>
            <div className="sub-tabs">
              {Object.keys(tabConfig[activeMainTab].subTabs).map((subTab) => (
                <button
                  key={subTab}
                  onClick={() => setActiveSubTab(subTab)}
                  className={activeSubTab === subTab ? "active" : ""}
                >
                  {tabConfig[activeMainTab].subTabs[subTab]}
                </button>
              ))}
            </div>
          </div>

          <div className="tab-content">
            {/* Keep all components mounted but hidden for instant tab switching */}
            <div style={{ display: activeSubTab === "disallowedTransactions" ? 'block' : 'none' }}>
              <DisallowedTransactionsFrame username={username} />
            </div>
            <div style={{ display: activeSubTab === "washSaleAvoidance" ? 'block' : 'none' }}>
              <WashSaleAvoidance 
                username={username} 
                isUnlocked={isDashboardUnlocked}
                onUnlockSuccess={handleUnlockSuccess}
                hasFreeAccess={hasFreeAccess}
                userTier={userTier}
              />
            </div>
            <div style={{ display: activeSubTab === "realizedGains" ? 'block' : 'none' }}>
              <CapitalGainTransactionsFrame username={username} />
            </div>
            <div style={{ display: activeSubTab === "realizedLosses" ? 'block' : 'none' }}>
              <CapitalLossTransactionsFrame username={username} />
            </div>
            <div style={{ display: activeSubTab === "unrealizedLosses" ? 'block' : 'none' }}>
              {/* Only show progress indicator during active onboarding processing */}
              {/* Otherwise, always show the component - it handles its own loading/empty states internally */}
              {isOnboardingProcessing && settledTransactionsLoading && settledTransactionsProgress ? (
                <TransactionProgressIndicator 
                  progress={settledTransactionsProgress} 
                  loading={settledTransactionsLoading}
                />
              ) : (
                <TaxLossOpportunitiesFrame 
                  username={username} 
                  isUnlocked={isDashboardUnlocked}
                  onUnlockSuccess={handleUnlockSuccess}
                  hasFreeAccess={hasFreeAccess}
                  userTier={userTier}
                />
              )}
            </div>
            <div style={{ display: activeSubTab === "unrealizedGains" ? 'block' : 'none' }}>
              <UnrealizedCapitalGainsFrame username={username} />
            </div>
          </div>
        </div>
      </div>
      {/* Pass setIsOnboardingProcessing to Modal */}
      {/* <Modal isOpen={modalOpen} onClose={() => setModalOpen({ open: false })} username={username} setIsOnboardingProcessing={setIsOnboardingProcessing} /> */}
    </PrimeReactProvider>
  ) : accountsCheckComplete ? (
    // Only show journey page after confirming no accounts exist
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="dashboard-container dashboard-container--no-accounts" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9' }}>
      <div className="content-wrapper" style={{ maxWidth: 480, width: '100%', margin: '0 auto', padding: 24 }}>
        {/* User Journey Progress Section - Modern Horizontal Stepper (fixed alignment) */}
        <div className="journey-section" style={{
          background: '#eafaf7',
          borderRadius: 14,
          padding: '28px 18px 18px 18px',
          marginBottom: 32,
          boxShadow: '0 2px 12px rgba(42,157,143,0.07)',
        }}>
          <h3 className="journey-title" style={{ color: '#264653', fontWeight: 700, fontSize: 18, margin: '0 0 18px 0', textAlign: 'center', letterSpacing: 0.2 }}>
            Your ProfiTree Journey
          </h3>
          {/* Stepper Circles and Connectors */}
          <div className="journey-stepper" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: 380,
            margin: '0 auto',
            position: 'relative',
            height: 44,
          }}>
            {/* Step 1: Done */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div className="journey-step-circle" style={{
                background: '#2a9d8f',
                color: '#fff',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                border: '2.5px solid #2a9d8f',
                boxShadow: '0 2px 8px rgba(42,157,143,0.10)'
              }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" fill="#2a9d8f"/><path d="M6 10.5L9 13.5L14 8.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
            {/* Connector */}
            <div style={{ height: 2, background: '#2a9d8f', opacity: 0.3, flex: 1, minWidth: 20, maxWidth: 40 }} />
            {/* Step 2 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div className="journey-step-circle" style={{
                background: '#fff',
                color: '#2a9d8f',
                border: '2.5px solid #2a9d8f',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
              }}>2</div>
            </div>
            {/* Connector */}
            <div style={{ height: 2, background: '#2a9d8f', opacity: 0.3, flex: 1, minWidth: 20, maxWidth: 40 }} />
            {/* Step 3 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div className="journey-step-circle" style={{
                background: '#fff',
                color: '#2a9d8f',
                border: '2.5px solid #2a9d8f',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
              }}>3</div>
            </div>
            {/* Connector */}
            <div style={{ height: 2, background: '#2a9d8f', opacity: 0.3, flex: 1, minWidth: 20, maxWidth: 40 }} />
            {/* Step 4 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div className="journey-step-circle" style={{
                background: '#fff',
                color: '#2a9d8f',
                border: '2.5px solid #2a9d8f',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
              }}>4</div>
            </div>
          </div>
          {/* Step Labels */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            maxWidth: 380,
            margin: '8px auto 0 auto',
            gap: 0,
          }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div className="journey-step-label-main" style={{ color: '#264653', fontWeight: 600, fontSize: 14 }}>Sign up</div>
              <div className="journey-step-label" style={{ color: '#2a9d8f', fontSize: 11, fontWeight: 500 }}>(done)</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div className="journey-step-label-main" style={{ color: '#264653', fontWeight: 600, fontSize: 14 }}>Link account</div>
              <div className="journey-step-label" style={{ color: '#264653', fontSize: 11, fontWeight: 400 }}>(avoid manual syncing)</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div className="journey-step-label-main" style={{ color: '#264653', fontWeight: 600, fontSize: 14 }}>Review tax insights</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div className="journey-step-label-main" style={{ color: '#264653', fontWeight: 600, fontSize: 14 }}>Action for tax savings</div>
            </div>
          </div>

        </div>
        {/* Option 1: Link Account */}
        <div className="link-account-card" style={{
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(38,70,83,0.10)',
          padding: '36px 28px',
          textAlign: 'center',
          border: '1.5px solid #e0e4e7',
          marginBottom: '24px',
        }}>
          <div className="link-account-badge" style={{ 
            background: '#2a9d8f', 
            color: '#fff', 
            padding: '6px 16px', 
            borderRadius: '20px', 
            fontSize: '12px', 
            fontWeight: 700, 
            marginBottom: '16px',
            display: 'inline-block',
            letterSpacing: 0.5
          }}>
            OPTION 1 - RECOMMENDED
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
            <div className="link-account-icon" style={{
              background: '#2a9d8f',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
              boxShadow: '0 2px 8px rgba(42,157,143,0.10)'
            }}>
              <span style={{ fontSize: 32, color: '#fff' }}>🔒</span>
            </div>
            <h2 className="link-account-title" style={{ color: '#264653', fontWeight: 700, fontSize: 24, margin: 0 }}>Link Your Brokerage Account</h2>
          </div>
          <p className="link-account-description" style={{ color: '#264653', fontSize: 17, margin: '12px 0 0 0', fontWeight: 500 }}>
            ProfiTree needs to securely connect to your brokerage account to provide you with accurate, personalized tax insights based on your real investment data.
          </p>
          <ul className="link-account-list" style={{ textAlign: 'left', margin: '22px 0 0 0', padding: '0 0 0 18px', color: '#264653', fontSize: 15, lineHeight: 1.7 }}>
            <li><strong>Bank-level security:</strong> We use SnapTrade (a Plaid-like, YC-backed provider) to securely connect your brokerage account. Your data is encrypted and protected. ProfiTree uses read-only access—your credentials are never stored.</li>
            <li><strong>Private & confidential:</strong> We never make trades or move money. You can unlink your account at any time.</li>
            <li><strong>Simple process:</strong> Click "Link Account" below and follow the secure steps. Your tax insights will be ready in minutes!</li>
          </ul>
          




          <div style={{ marginTop: 28, marginBottom: 10 }}>
            <button className="btn-punchy btn-punchy-primary link-account-button" style={{ width: 220, fontSize: 18, background: '#2a9d8f', color: '#fff', border: 'none', borderRadius: 8, boxShadow: '0 4px 16px rgba(42,157,143,0.10)', fontWeight: 700, padding: '14px 0', letterSpacing: 0.2 }} onClick={handleAddFirstAccount}>
              Link Account
            </button>
          </div>
          <div className="link-account-security-badge" style={{ marginTop: 8, fontSize: 13, color: '#2a9d8f', background: '#eafaf7', borderRadius: 8, padding: '10px 14px', display: 'inline-block', fontWeight: 500 }}>
            <span style={{ marginRight: 6, fontSize: 16 }}>✔️</span>
            Your connection is always secure and read-only
          </div>
          
          {/* Upload Option Toggle */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <button 
              className="upload-toggle-button"
              onClick={() => setShowUploadOption(!showUploadOption)}
              style={{ 
                background: 'transparent', 
                border: '2px solid #2a9d8f', 
                color: '#2a9d8f',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(42,157,143,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2a9d8f';
                e.target.style.color = '#fff';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(42,157,143,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#2a9d8f';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(42,157,143,0.1)';
              }}
            >
              {showUploadOption ? 'Hide Manual Upload' : '📁 Try Option 2: Manual Upload'}
            </button>
          </div>
        </div>
        
        {/* Option 2: Manual Upload Form */}
        {showUploadOption && (
          <div className="upload-section" style={{
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: 16,
            padding: '24px',
            marginTop: '24px',
            border: '2px solid #e9ecef',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '16px'
            }}>
              <div style={{ 
                background: 'transparent', 
                color: '#2a9d8f', 
                padding: '6px 16px', 
                borderRadius: '20px', 
                fontSize: '12px', 
                fontWeight: 700, 
                letterSpacing: 0.5,
                border: '2px solid #2a9d8f'
              }}>
                OPTION 2 - MANUAL UPLOAD
              </div>
              <div style={{ 
                background: '#2a9d8f', 
                color: '#fff', 
                padding: '4px 8px', 
                borderRadius: '12px', 
                fontSize: '10px', 
                fontWeight: 700, 
                letterSpacing: 0.5
              }}>
                BETA
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{
                background: '#2a9d8f',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 12,
                boxShadow: '0 2px 8px rgba(42,157,143,0.2)'
              }}>
                <span style={{ fontSize: 16, color: '#fff' }}>📄</span>
              </div>
              <h4 className="upload-section-title" style={{ color: '#264653', fontWeight: 700, fontSize: 18, margin: 0 }}>
                Upload Transaction/Statement Files
              </h4>
            </div>
            
            <p className="upload-section-description" style={{ color: '#6c757d', fontSize: 15, margin: '0 0 20px 0', lineHeight: 1.5 }}>
              Upload one or more CSV or PDF statement files (1099-B format supported)
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <label className="upload-file-label" style={{ 
                display: 'block', 
                color: '#264653', 
                fontWeight: 600, 
                fontSize: 14, 
                marginBottom: 8 
              }}>
                Statement files (CSV or PDF)
              </label>
              <div className="upload-file-dropzone" style={{
                border: '2px dashed #2a9d8f',
                borderRadius: 12,
                padding: '20px',
                textAlign: 'center',
                background: 'rgba(42,157,143,0.05)',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1a7a6f';
                e.currentTarget.style.background = 'rgba(42,157,143,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#2a9d8f';
                e.currentTarget.style.background = 'rgba(42,157,143,0.05)';
              }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.pdf,text/csv,application/pdf"
                  multiple
                  onChange={(e) => {
                    const list = e.target.files ? Array.from(e.target.files) : [];
                    const allowed = list.filter(isAllowedStatementFile);
                    const rejected = list.filter((f) => !isAllowedStatementFile(f));
                    if (rejected.length > 0) {
                      setUploadError(`Only CSV and PDF are allowed. Skipped: ${rejected.map((f) => f.name).join(", ")}`);
                      toast.error("Only CSV and PDF files are allowed. Other files were not added.");
                    } else {
                      setUploadError(null);
                    }
                    setSelectedFiles((prev) => {
                      const combined = [...prev];
                      for (const f of allowed) {
                        const isDuplicate = combined.some(
                          (ex) => ex.name === f.name && ex.size === f.size && ex.lastModified === f.lastModified
                        );
                        if (!isDuplicate) combined.push(f);
                      }
                      return combined;
                    });
                    e.target.value = "";
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 0
                  }}
                />
                <div className="upload-file-icon" style={{ color: '#2a9d8f', fontSize: 24, marginBottom: 8 }}>📁</div>
                <div className="upload-file-name" style={{ color: '#264653', fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                  {selectedFiles.length === 0
                    ? 'Choose one or more files'
                    : selectedFiles.length === 1
                      ? selectedFiles[0].name
                      : `${selectedFiles.length} files selected`}
                </div>
                <div className="upload-file-hint" style={{ color: '#6c757d', fontSize: 14 }}>
                  {selectedFiles.length === 0 ? 'CSV and PDF only' : 'Click above or add more below'}
                </div>
                {selectedFiles.length > 0 && (
                  <div
                    style={{ marginTop: 12, textAlign: 'left', position: 'relative', zIndex: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {selectedFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '6px 10px',
                          marginBottom: 4,
                          background: 'rgba(255,255,255,0.8)',
                          borderRadius: 6,
                          fontSize: 13,
                          color: '#264653'
                        }}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
                          }}
                          style={{
                            flexShrink: 0,
                            padding: '2px 8px',
                            border: '1px solid #dc3545',
                            borderRadius: 6,
                            background: '#fff',
                            color: '#dc3545',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                          aria-label={`Remove ${file.name}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        style={{
                          padding: '4px 0',
                          border: 'none',
                          background: 'none',
                          color: '#2a9d8f',
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Add more files
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFiles([]);
                        }}
                        style={{
                          padding: '4px 0',
                          border: 'none',
                          background: 'none',
                          color: '#6c757d',
                          fontSize: 12,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <label className="upload-select-label" style={{ 
                display: 'block', 
                color: '#264653', 
                fontWeight: 600, 
                fontSize: 14, 
                marginBottom: 8 
              }}>
                Brokerage <span style={{ color: '#6c757d', fontWeight: 500 }}>(optional)</span>
              </label>
              <select
                className="upload-select"
                value={brokerageName}
                onChange={(e) => setBrokerageName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2a9d8f';
                  e.target.style.boxShadow = '0 0 0 3px rgba(42,157,143,0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select a brokerage (optional)</option>
                <option value="Fidelity">Fidelity</option>
                <option value="Robinhood">Robinhood</option>
                <option value="Schwab">Schwab</option>
                <option value="Etrade">E*TRADE</option>
                <option value="Merrill Lynch">Merrill Lynch</option>
              </select>
            </div>
            
            <div className="upload-info-box" style={{ 
              marginTop: '16px', 
              padding: '16px 20px', 
              background: '#f8f9fa', 
              borderRadius: '8px', 
              border: '1px solid #e9ecef',
              fontSize: '13px',
              color: '#6c757d',
              lineHeight: 1.5
            }}>
              If you use a different brokerage than the options above, please feel free to <a href="/contact-us" style={{ color: '#2a9d8f', textDecoration: 'underline', fontWeight: 600 }}>reach out</a>. We can help you!
            </div>

            {uploadError && (
              <div style={{
                background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                color: '#721c24',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                border: '1px solid #f5c6cb',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                {uploadError}
              </div>
            )}

            <button 
              className="upload-submit-button"
              onClick={handleFileUpload}
              disabled={uploadLoading || !selectedFiles.length}
              style={{
                background: uploadLoading || !selectedFiles.length
                  ? 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)' 
                  : 'linear-gradient(135deg, #2a9d8f 0%, #1a7a6f 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '14px 24px',
                cursor: uploadLoading || !selectedFiles.length ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 700,
                width: '100%',
                transition: 'all 0.2s ease',
                boxShadow: uploadLoading || !selectedFiles.length
                  ? '0 2px 4px rgba(108,117,125,0.2)' 
                  : '0 4px 12px rgba(42,157,143,0.3)',
                letterSpacing: 0.5
              }}
              onMouseEnter={(e) => {
                if (!uploadLoading && selectedFiles.length > 0) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(42,157,143,0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!uploadLoading && selectedFiles.length > 0) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(42,157,143,0.3)';
                }
              }}
            >
              {uploadLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 16,
                    height: 16,
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Processing...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  Upload and process
                </span>
              )}
            </button>
          </div>
        )}

        {showUploadResultModal && uploadResult && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 9999
            }}
            onClick={cancelUploadResult}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 520,
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 16px 60px rgba(0,0,0,0.25)',
                border: '1px solid rgba(0,0,0,0.08)',
                padding: 20
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#264653' }}>Upload results</div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#6c757d', lineHeight: 1.4 }}>
                    We received your file(s). Here’s what the backend reported.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={cancelUploadResult}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    fontSize: 22,
                    lineHeight: 1,
                    cursor: 'pointer',
                    color: '#6c757d'
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Layer used', value: uploadResult.layerUsed ?? '—' },
                  { label: 'Data rows attempted', value: uploadResult.dataRowsAttempted ?? 0 },
                  { label: 'Transactions produced', value: uploadResult.transactionsProduced ?? 0 },
                  { label: 'Validation passed', value: uploadResult.validationPassedCount ?? 0 },
                  // Optional: only show when backend includes it
                  ...(uploadResult.nonTradeActivityCount != null
                    ? [{ label: 'Non-trade activity count', value: uploadResult.nonTradeActivityCount }]
                    : []),
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      border: '1px solid #e9ecef',
                      borderRadius: 12,
                      padding: '10px 12px',
                      background: '#f8f9fa'
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#6c757d', fontWeight: 700, letterSpacing: 0.2 }}>
                      {item.label}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14, color: '#264653', fontWeight: 800 }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={cancelUploadResult}
                  style={{
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: '1px solid #ced4da',
                    background: '#fff',
                    color: '#264653',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={proceedFromUploadResult}
                  style={{
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #2a9d8f 0%, #1a7a6f 100%)',
                    color: '#fff',
                    fontWeight: 900,
                    cursor: 'pointer'
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  ) : (
    // Show loading state while checking for accounts
    <div className="dashboard-container" style={{ 
      minHeight: '70vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: '#f9f9f9' 
    }}>
      <div style={{ 
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '16px'
      }}>
        <div style={{
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #2a9d8f',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p>Checking for accounts...</p>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
      )}
      
      {/* Account Connection Modal */}
      <AccountConnectionModal
        isOpen={showAccountConnectionModal}
        onClose={() => setShowAccountConnectionModal(false)}
        onSnapTradeSelect={handleSnapTradeSelect}
        onPlaidSelect={handlePlaidSelect}
        username={username}
        onManualUploadSelect={handleManualUploadSelect}
      />

      {/* Plaid - render directly via portal, it will open Plaid Link interface automatically */}
      {showPlaidModal && createPortal(
        <PlaidLinkComponent
          linkToken={plaidLinkToken}
          onSuccess={handlePlaidSuccess}
          onClose={handlePlaidClose}
        />,
        document.body
      )}

    </>
  );
}

export default Dashboard;
